import { Injectable, OnDestroy } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { BehaviorSubject, Observable, Subscription, filter, first } from "rxjs";
import { apiCallWrapper } from "../api/api.util";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import { HasId } from "../model/generics";
import { CategoryApi } from "../api/category.api";
import {
	ICategory,
	ICreateCategory,
} from "../model/ddb.model";
import { SubscriptionGroup } from "../util/subscriptionGroup";
import { SessionApi } from "../api/session.api";
import { LoadingService } from "./core/loading.service";

@Injectable()
export class CategoryService implements OnDestroy {
	public readonly allCategories$ = new BehaviorSubject(
		new QueryResult<ICategory & HasId<string>>()
	);
	private readonly subscriptionGroup = new SubscriptionGroup();

	private cacheRequest?: Subscription;

	constructor(
		private notifications: NotificationsService,
		private categoryApi: CategoryApi,
		private sessionApi: SessionApi,
		private loadingService: LoadingService
	) {
		// Monitor a list of every category for the current user
		this.monitorCategories();
	}

	ngOnDestroy(): void {
		this.cacheRequest?.unsubscribe();
		if (this.subscriptionGroup) {
			this.subscriptionGroup.unsubscribe();
		}
	}

	monitorCategories() {
		this.subscriptionGroup.add(
			// Refresh once after session data settles, including logout/account switches.
			this.sessionApi.$sessionChanged.pipe(filter((ready) => ready)).subscribe({
				next: () => {
					this.refreshCategoryCache();
				},
			})
		);
	}

	public refreshCategoryCache() {
		this.cacheRequest?.unsubscribe();
		this.allCategories$.next(new QueryResult<ICategory & HasId<string>>());
		this.cacheRequest = this.list(new IQueryFilter({ limit: 10000 }))
			.pipe(first())
			.subscribe({
				next: (result) => {
					this.allCategories$.next(new QueryResult(result));
				},
				error: () => {
					this.allCategories$.next(new QueryResult<ICategory & HasId<string>>());
				},
			});
	}

	public list(query: IQueryFilter) {
		if (!this.sessionApi.$userData.getValue()) {
			return this.unAuthlist(query);
		}
		return apiCallWrapper(this.categoryApi.list(query));
	}

	public unAuthlist(query: IQueryFilter) {
		return apiCallWrapper(this.categoryApi.unAuthlist(query));
	}

	public create(data: ICreateCategory): Observable<ICategory & HasId<string>> {
		return apiCallWrapper(
			this.loadingService.blockWithLoadingOverlayRx(
				this.categoryApi.create(data)
			),
			{
				notificationsService: this.notifications,
				action: "Creating Category",
			}
		);
	}

	public update(
		data: ICreateCategory,
		id: string
	): Observable<ICategory & HasId<string>> {
		return apiCallWrapper(
			this.loadingService.blockWithLoadingOverlayRx(
				this.categoryApi.update(id, data)
			),
			{
				notificationsService: this.notifications,
				action: "Updating Category",
			}
		);
	}

	public delete(id: string) {
		return apiCallWrapper(
			this.loadingService.blockWithLoadingOverlayRx(
				this.categoryApi.delete(id)
			),
			{
				notificationsService: this.notifications,
				action: "Deleting Category",
			}
		);
	}

	public restore(id: number | string) {
		return apiCallWrapper(
			this.loadingService.blockWithLoadingOverlayRx(this.categoryApi.restore(id)),
			{
				notificationsService: this.notifications,
				action: "Restore Category",
			}
		);
	}
}
